'use client'

// SUMMARY: Renders assistant chat content as Markdown with styled code blocks and copy-to-clipboard

import { memo, useEffect, useState, type ReactNode } from 'react'
import ReactMarkdown, { defaultUrlTransform, type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Check, Copy } from 'lucide-react'
import { highlightCode } from '@/lib/shiki'
import { sanitizeAssistantMarkdown } from '@/lib/ai/sanitize-markdown'

function extractText(children: ReactNode): string {
	if (typeof children === 'string') return children
	if (Array.isArray(children)) return children.map(extractText).join('')
	if (children && typeof children === 'object' && 'props' in children) {
		return extractText((children as { props: { children: ReactNode } }).props.children)
	}
	return ''
}

/** Pull the raw code text and language hint out of a markdown <pre>'s <code> child */
function extractCodeMeta(children: ReactNode): { code: string; lang?: string } {
	const child = Array.isArray(children) ? children[0] : children
	if (child && typeof child === 'object' && 'props' in child) {
		const props = (child as { props: { className?: string; children?: ReactNode } }).props
		const lang = /language-([\w-]+)/.exec(props.className || '')?.[1]
		return { code: extractText(props.children).replace(/\n$/, ''), lang }
	}
	return { code: extractText(children).replace(/\n$/, '') }
}

function CodeBlock({ code, lang }: { code: string; lang?: string }) {
	const [copied, setCopied] = useState(false)
	const [html, setHtml] = useState<string | null>(null)

	useEffect(() => {
		let cancelled = false
		highlightCode({ code, lang })
			.then((result) => {
				if (!cancelled) setHtml(result)
			})
			.catch(() => {})
		return () => {
			cancelled = true
		}
	}, [code, lang])

	const copy = async () => {
		await navigator.clipboard.writeText(code)
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
	}

	return (
		<div className="my-3 overflow-hidden rounded-lg border border-border/60">
			<div className="flex items-center justify-between bg-foreground/5 px-3 py-1.5">
				<span className="font-mono text-xs text-muted-foreground">{lang || 'text'}</span>
				<button
					onClick={copy}
					className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
					aria-label="Copy code"
				>
					{copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
				</button>
			</div>
			{html ? (
				<div
					className="overflow-x-auto text-[0.8rem] leading-relaxed [&_code]:font-mono [&_pre]:m-0 [&_pre]:p-3"
					dangerouslySetInnerHTML={{ __html: html }}
				/>
			) : (
				<pre className="overflow-x-auto bg-foreground/5 p-3 text-[0.8rem] leading-relaxed">
					<code className="font-mono">{code}</code>
				</pre>
			)}
		</div>
	)
}

const COMPONENTS: Components = {
	p: ({ children }) => <p className="mb-3 leading-relaxed last:mb-0">{children}</p>,
	ul: ({ children }) => <ul className="mb-3 list-disc space-y-1.5 pl-5 last:mb-0">{children}</ul>,
	ol: ({ children }) => <ol className="mb-3 list-decimal space-y-1.5 pl-5 last:mb-0">{children}</ol>,
	li: ({ children }) => <li className="leading-relaxed [&>ul]:mt-1.5 [&>ol]:mt-1.5">{children}</li>,
	strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
	em: ({ children }) => <em className="italic">{children}</em>,
	a: ({ children, href }) => (
		<a href={href} target="_blank" rel="noopener noreferrer" className="font-medium underline underline-offset-2">
			{children}
		</a>
	),
	h1: ({ children }) => (
		<h3 className="mb-2 mt-5 border-b border-border pb-1 text-lg font-bold tracking-tight first:mt-0">{children}</h3>
	),
	h2: ({ children }) => (
		<h3 className="mb-2 mt-5 border-b border-border/60 pb-1 text-base font-semibold tracking-tight first:mt-0">
			{children}
		</h3>
	),
	h3: ({ children }) => <h4 className="mb-1.5 mt-4 text-[0.95rem] font-semibold first:mt-0">{children}</h4>,
	h4: ({ children }) => (
		<h5 className="mb-1 mt-3 text-sm font-semibold text-muted-foreground first:mt-0">{children}</h5>
	),
	blockquote: ({ children }) => (
		<blockquote className="my-2 border-l-2 border-border pl-3 text-muted-foreground">{children}</blockquote>
	),
	hr: () => <hr className="my-3 border-border" />,
	table: ({ children }) => (
		<div className="my-3 overflow-x-auto">
			<table className="w-full border-collapse text-sm">{children}</table>
		</div>
	),
	th: ({ children }) => <th className="border border-border px-2 py-1 text-left font-semibold">{children}</th>,
	td: ({ children }) => <td className="border border-border px-2 py-1">{children}</td>,
	code: ({ className, children }) => {
		const isBlock = className?.includes('language-')
		if (isBlock) return <>{children}</>
		return <code className="rounded bg-foreground/10 px-1 py-0.5 font-mono text-[0.85em]">{children}</code>
	},
	pre: ({ children }) => {
		const { code, lang } = extractCodeMeta(children)
		return <CodeBlock code={code} lang={lang} />
	},
	img: ({ src, alt }) => (
		// AI-generated images arrive as base64 data URLs; next/image can't optimize those
		// eslint-disable-next-line @next/next/no-img-element
		<img
			src={typeof src === 'string' ? src : ''}
			alt={alt || ''}
			className="my-2 max-h-[28rem] w-auto max-w-full rounded-lg border"
		/>
	),
}

/** Allow base64 image data URLs (FLUX output) while keeping the default safety filter for everything else */
function transformUrl(url: string): string {
	return url.startsWith('data:image/') ? url : defaultUrlTransform(url)
}

export const ChatMarkdown = memo(function ChatMarkdown({ content }: { content: string }) {
	return (
		<ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS} urlTransform={transformUrl}>
			{content}
		</ReactMarkdown>
	)
})
